import { useCallback, useContext, useEffect } from "react"

import { AppContext } from "../App"
import { GREEK_KEYBOARD_ROWS, LetterStatus } from "../helpers"
import { isGreekLetterLike, toNFC } from "../lib/greekText"
import Key from "./Key"

const ENGLISH_TO_GREEK_DESKTOP: Record<string, string> = {
  a: "α",
  b: "β",
  g: "γ",
  d: "δ",
  e: "ε",
  z: "ζ",
  h: "η",
  q: "θ",
  i: "ι",
  k: "κ",
  l: "λ",
  m: "μ",
  n: "ν",
  c: "ξ",
  o: "ο",
  p: "π",
  r: "ρ",
  s: "σ",
  t: "τ",
  u: "υ",
  f: "φ",
  x: "χ",
  y: "ψ",
  w: "ω",
}

function isDesktopKeyboardMode(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }

  return window.matchMedia("(pointer: fine)").matches
}

function transliterateDesktopKey(
  rawKey: string,
  isWordFinalPosition: boolean
): string | null {
  const normalizedKey = rawKey.toLowerCase()

  if (normalizedKey === "s") {
    return isWordFinalPosition ? "ς" : "σ"
  }

  return ENGLISH_TO_GREEK_DESKTOP[normalizedKey] || null
}

function Keyboard() {
  const {
    onDelete,
    onEnter,
    onSelectLetter,
    letterStatus,
    currAttempt,
    settings,
  } = useContext(AppContext)

  const handleKeyboard = useCallback(
    (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) {
        return
      }

      if (event.key === "Enter") {
        onEnter()
        return
      }

      if (event.key === "Backspace") {
        onDelete()
        return
      }

      const normalizedKey = toNFC(event.key)
      if (isGreekLetterLike(normalizedKey)) {
        onSelectLetter(normalizedKey)
        return
      }

      if (!isDesktopKeyboardMode()) {
        return
      }

      const isWordFinalPosition = currAttempt.letterPos === settings.wordLength - 1
      const mappedLetter = transliterateDesktopKey(event.key, isWordFinalPosition)
      if (mappedLetter) {
        onSelectLetter(mappedLetter)
      }
    },
    [currAttempt.letterPos, onDelete, onEnter, onSelectLetter, settings.wordLength]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyboard)

    return () => {
      document.removeEventListener("keydown", handleKeyboard)
    }
  }, [handleKeyboard])

  return (
    <div className="keyboard" data-testid="keyboard">
      {GREEK_KEYBOARD_ROWS.map((row, rowIndex) => (
        <div className="keyboard-row" key={`row-${rowIndex}`}>
          {rowIndex === GREEK_KEYBOARD_ROWS.length - 1 ? (
            <Key keyVal="ENTER" bigKey status={LetterStatus.Unknown} />
          ) : null}
          {row.map((key) => (
            <Key
              key={key}
              keyVal={key}
              status={letterStatus.get(key) || LetterStatus.Unknown}
            />
          ))}
          {rowIndex === GREEK_KEYBOARD_ROWS.length - 1 ? (
            <Key keyVal="DELETE" bigKey status={LetterStatus.Unknown} />
          ) : null}
        </div>
      ))}
    </div>
  )
}

export default Keyboard
